import React from 'react';
import { ListRenderItem, StyleSheet, View } from 'react-native';

import { useActionSheet } from '@components/ActionSheet';
import { AvatarIcon, AvatarImage } from '@components/Avatar';
import Badge from '@components/Badge';
import { Button } from '@components/Button';
import Divider from '@components/Divider';
import FlatList from '@components/FlatList';
import IconButton from '@components/IconButton';
import Placeholder from '@components/Placeholders';
import { PressableOpacity } from '@components/Pressable';
import ScreenContainer from '@components/ScreenContainer';
import { Caption, Paragraph } from '@components/Typography';
import { Row } from '@components/View';
import { useNotifications } from '@context/NotificationsContext';
import { deleteNotification, updateNotificationRead } from '@db/Notifications';
import {
  RequestResponseMutationAction,
  useFollowRequestResponseMutation,
  useJoinGroupRequestResponseMutation,
  useJoinPlanRequestResponseMutation,
} from '@db/Requests/Queries';
import { errorAlert } from '@helpers/Alert';
import { dateFormatDistanceToNowStrict } from '@helpers/Dates';
import { logError } from '@helpers/Loggers';
import { AppNavigation, createAppNavigation } from '@navigation/AppNavigation';
import { NotificationScreenProps, ViewPlanScreenToggleState } from '@navigation/Types';
import { Notification, NotificationType } from '@types';

export default function NotificationsScreen({ navigation }: NotificationScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const { notifications, requestPermissionAndSync } = useNotifications();
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    requestPermissionAndSync().catch((error) => logError(error));
  }, [requestPermissionAndSync]);

  const actionSheetOptions = {
    options: ['Mark All Read', 'Delete All', 'Cancel'],
    cancelButtonIndex: 2,
  };

  async function markAllNotificationsRead() {
    setLoading(true);
    try {
      await Promise.all(
        notifications
          .filter((notification) => !notification.read)
          .map((notification) => updateNotificationRead(notification.notificationId))
      );
    } catch (error) {
      logError(error);
    }
    setLoading(false);
  }

  async function deleteAllNotifications() {
    setLoading(true);
    try {
      await Promise.all(
        notifications.map((notification) => deleteNotification(notification.notificationId))
      );
    } catch (error) {
      logError(error);
    }
    setLoading(false);
  }

  const actionSheetCallback = async (selectedIndex?: number) => {
    switch (selectedIndex) {
      case 0:
        return await markAllNotificationsRead();
      case 1:
        return await deleteAllNotifications();
    }
  };

  const { showActionSheet } = useActionSheet(actionSheetOptions, actionSheetCallback);

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => <IconButton icon="dots-vertical" onPress={() => showActionSheet()} />,
    });
  }, [navigation, showActionSheet]);

  const renderItem: ListRenderItem<Notification> = ({ item }) => (
    <NotificationItem appNavigation={appNavigation} notification={item} />
  );

  const keyExtractor = (item: Notification) => item.notificationId;

  return (
    <ScreenContainer loading={loading} testID="screens.NotificationsScreen.Background">
      <FlatList
        data={notifications}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        initialNumToRender={10}
        maxToRenderPerBatch={3}
        windowSize={5}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        ItemSeparatorComponent={() => <Divider style={styles.divider} />}
        ListEmptyComponent={
          <Placeholder
            title="No notifications"
            icon="bell"
            message="Your notifications will appear here"
          />
        }
        testID="screens.NotificationsScreen.FlatList.Notifications"
      />
    </ScreenContainer>
  );
}

const AVATAR_SIZE = 30;
const BADGE_SIZE = 10;

type NotificationItemProps = {
  appNavigation: Pick<AppNavigation, 'openGroup' | 'openPlan' | 'openProfile'>;
  notification: Notification;
};

type NotificationBehavior = {
  icon: string;
  onPress: () => void;
  onAccept?: () => Promise<boolean>;
  onReject?: () => Promise<boolean>;
};

function NotificationItem({ appNavigation, notification }: NotificationItemProps) {
  const [optimisticRead, setOptimisticRead] = React.useState<
    { baseRead: boolean; notificationId: string; read: boolean } | undefined
  >();
  const read =
    optimisticRead?.notificationId === notification.notificationId &&
    optimisticRead.baseRead === notification.read
      ? optimisticRead.read
      : notification.read;
  const [accepting, setAccepting] = React.useState<boolean>(false);
  const [rejecting, setRejecting] = React.useState<boolean>(false);
  const followRequestResponseMutation = useFollowRequestResponseMutation();
  const joinGroupRequestResponseMutation = useJoinGroupRequestResponseMutation();
  const joinPlanRequestResponseMutation = useJoinPlanRequestResponseMutation();

  const { entityId, request, fromUserId, notificationId } = notification;

  const handleRequestResponseResult = (result: { error?: unknown; ok: boolean }) => {
    if (!result.ok) {
      errorAlert(result.error);
    }

    return result.ok;
  };

  const behaviorByType = (type: Notification['type']): NotificationBehavior => {
    switch (type) {
      case NotificationType.LIKE:
        return {
          icon: 'heart',
          onPress: () =>
            appNavigation.openPlan(entityId, {
              toggleState: ViewPlanScreenToggleState.LIKES,
            }),
        };
      case NotificationType.COMMENT:
        return {
          icon: 'comment',
          onPress: () =>
            appNavigation.openPlan(entityId, {
              toggleState: ViewPlanScreenToggleState.COMMENTS,
            }),
        };

      case NotificationType.FOLLOW:
        return {
          icon: 'account',
          onPress: () => appNavigation.openProfile(fromUserId),
        };
      case NotificationType.FOLLOW_REQUEST:
        return {
          icon: 'account',
          onPress: () => appNavigation.openProfile(fromUserId),
          onAccept: request
            ? async () => {
                const result = await followRequestResponseMutation.mutateAsync({
                  action: RequestResponseMutationAction.ACCEPT,
                  userId: request.userId,
                });
                return handleRequestResponseResult(result);
              }
            : undefined,
          onReject: request
            ? async () => {
                const result = await followRequestResponseMutation.mutateAsync({
                  action: RequestResponseMutationAction.REJECT,
                  userId: request.userId,
                });
                return handleRequestResponseResult(result);
              }
            : undefined,
        };

      case NotificationType.GROUP_JOIN:
        return {
          icon: 'account-multiple',
          onPress: () => appNavigation.openGroup(entityId),
        };
      case NotificationType.GROUP_REQUEST:
        return {
          icon: 'account-multiple',
          onPress: () => appNavigation.openProfile(fromUserId),
          onAccept: request
            ? async () => {
                const result = await joinGroupRequestResponseMutation.mutateAsync({
                  action: RequestResponseMutationAction.ACCEPT,
                  groupId: request.entityId,
                  userId: request.userId,
                });
                return handleRequestResponseResult(result);
              }
            : undefined,
          onReject: request
            ? async () => {
                const result = await joinGroupRequestResponseMutation.mutateAsync({
                  action: RequestResponseMutationAction.REJECT,
                  groupId: request.entityId,
                  userId: request.userId,
                });
                return handleRequestResponseResult(result);
              }
            : undefined,
        };

      case NotificationType.PLAN:
      case NotificationType.PLAN_OVERLAP:
      case NotificationType.PLAN_JOIN:
      case NotificationType.PLAN_INVITE:
        return {
          icon: 'sign-direction',
          onPress: () =>
            appNavigation.openPlan(entityId, {
              reload: true,
              toggleState: ViewPlanScreenToggleState.JOINED,
            }),
        };
      case NotificationType.PLAN_JOIN_REQUEST:
        return {
          icon: 'sign-direction',
          onPress: () => appNavigation.openProfile(fromUserId),
          onAccept: request
            ? async () => {
                const result = await joinPlanRequestResponseMutation.mutateAsync({
                  action: RequestResponseMutationAction.ACCEPT,
                  planId: request.entityId,
                  userId: request.userId,
                });
                return handleRequestResponseResult(result);
              }
            : undefined,
          onReject: request
            ? async () => {
                const result = await joinPlanRequestResponseMutation.mutateAsync({
                  action: RequestResponseMutationAction.REJECT,
                  planId: request.entityId,
                  userId: request.userId,
                });
                return handleRequestResponseResult(result);
              }
            : undefined,
        };

      default:
        return {
          icon: 'bell',
          onPress: () => null,
        };
    }
  };

  const { icon, onPress, onAccept, onReject } = behaviorByType(notification.type);
  const actionDisabled = accepting || rejecting;

  const _onPress = async () => {
    if (!read) {
      setOptimisticRead({
        baseRead: notification.read,
        notificationId,
        read: true,
      });

      try {
        await updateNotificationRead(notificationId);
      } catch (error) {
        logError(error);
      }
    }

    try {
      onPress();
    } catch (error) {
      logError(error);
    }
  };

  const onPressAccept = async () => {
    if (!onAccept) return;

    setAccepting(true);
    let requestSucceeded = false;
    try {
      requestSucceeded = await onAccept();
    } catch {
      setAccepting(false);
      return;
    }
    if (!requestSucceeded) {
      setAccepting(false);
      return;
    }

    try {
      await deleteNotification(notificationId);
    } catch (error) {
      logError(error);
    }
    setAccepting(false);
  };

  const onPressReject = async () => {
    if (!onReject) return;

    setRejecting(true);
    let requestSucceeded = false;
    try {
      requestSucceeded = await onReject();
    } catch {
      setRejecting(false);
      return;
    }
    if (!requestSucceeded) {
      setRejecting(false);
      return;
    }

    try {
      await deleteNotification(notificationId);
    } catch (error) {
      logError(error);
    }
    setRejecting(false);
  };

  return (
    <PressableOpacity onPress={_onPress}>
      <View style={styles.notificationContainer}>
        <Row style={styles.flex}>
          <View style={styles.avatarContainer}>
            {read ? null : <Badge size={BADGE_SIZE} style={styles.badge} />}
            {notification.photoUrl ? (
              <AvatarImage source={notification.photoUrl} size={AVATAR_SIZE} />
            ) : (
              <AvatarIcon icon={icon} size={AVATAR_SIZE} />
            )}
          </View>
          <View style={styles.flex}>
            <Paragraph style={styles.flex}>{notification.text}</Paragraph>
            <Caption>{dateFormatDistanceToNowStrict(notification.createdAt) + ' ago'}</Caption>
          </View>
        </Row>
        {onAccept && onReject ? (
          <Row style={styles.buttonContainer}>
            <Button
              mode="contained"
              uppercase={false}
              onPress={onPressAccept}
              loading={accepting}
              disabled={actionDisabled}
              style={styles.button}>
              Accept
            </Button>
            <Button
              mode="outlined"
              uppercase={false}
              onPress={onPressReject}
              loading={rejecting}
              disabled={actionDisabled}
              style={styles.button}>
              Reject
            </Button>
          </Row>
        ) : null}
      </View>
    </PressableOpacity>
  );
}

const styles = StyleSheet.create({
  avatarContainer: {
    justifyContent: 'center',
    margin: 10,
  },
  badge: {
    left: -20,
    position: 'absolute',
  },
  button: {
    marginHorizontal: 10,
    marginTop: 10,
  },
  buttonContainer: {
    justifyContent: 'center',
  },
  divider: {
    alignSelf: 'center',
    width: '50%',
  },
  flex: {
    flex: 1,
  },
  list: {
    width: '100%',
  },
  listContent: {
    paddingBottom: 10,
  },
  notificationContainer: {
    flex: 1,
    marginVertical: 15,
    paddingHorizontal: 20,
  },
});
